import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import systemLogService from '../../services/systemLogService';

const SystemLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Filtres
  const [filters, setFilters] = useState({
    type: '',
    severity: '',
    userId: '',
    userEmail: ''
  });

  // Statistiques
  const [stats, setStats] = useState({
    total: 0,
    byType: {},
    bySeverity: {}
  });

  const severityColors = {
    info: 'info',
    success: 'success', 
    warning: 'warning',
    error: 'error'
  };

  const typeColors = {
    auth: 'primary',
    token: 'secondary',
    error: 'error',
    system: 'default',
    onedrive: 'info'
  };

  // Charger les logs
  const loadLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Construire les filtres pour la requête
      const queryFilters = {};
      if (filters.type) queryFilters.type = filters.type;
      if (filters.severity) queryFilters.severity = filters.severity;
      if (filters.userId) queryFilters.userId = filters.userId;
      
      const logsData = await systemLogService.getSystemLogs({
        ...queryFilters,
        limitResults: 200 // Charger plus de logs pour la pagination côté client
      });
      
      // Filtrage côté client pour userEmail (Firestore ne peut pas filtrer sur email)
      let filteredLogs = logsData;
      if (filters.userEmail) {
        filteredLogs = logsData.filter(log => 
          log.userEmail && log.userEmail.toLowerCase().includes(filters.userEmail.toLowerCase())
        );
      }
      
      setLogs(filteredLogs);
      
      // Calculer les statistiques
      const stats = {
        total: filteredLogs.length,
        byType: {},
        bySeverity: {}
      };
      
      filteredLogs.forEach(log => {
        stats.byType[log.type] = (stats.byType[log.type] || 0) + 1;
        stats.bySeverity[log.severity] = (stats.bySeverity[log.severity] || 0) + 1;
      });
      
      setStats(stats);
      
    } catch (err) {
      console.error('Erreur lors du chargement des logs:', err);
      setError('Impossible de charger les logs système. Vérifiez vos permissions.');
    } finally {
      setLoading(false);
    }
  };

  // Charger les logs au montage et lors des changements de filtres
  useEffect(() => {
    loadLogs();
  }, [filters]);

  // Gérer le changement de page
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Gérer le changement de lignes par page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Gérer les changements de filtres
  const handleFilterChange = (filterName) => (event) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: event.target.value
    }));
    setPage(0); // Reset pagination
  };

  // Réinitialiser les filtres
  const resetFilters = () => {
    setFilters({
      type: '',
      severity: '',
      userId: '',
      userEmail: ''
    });
  };

  // Exporter les logs au format JSON
  const exportLogs = () => {
    const dataStr = JSON.stringify(logs, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `system-logs-${format(new Date(), 'yyyy-MM-dd-HH-mm')}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  // Formater la date
  const formatDate = (date) => {
    if (!date) return 'Date inconnue';
    try {
      return format(new Date(date), 'dd/MM/yyyy HH:mm:ss', { locale: fr });
    } catch (error) {
      return 'Date invalide';
    }
  };

  // Calculer les logs à afficher pour la pagination
  const displayedLogs = logs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          📊 Logs Système
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Monitoring et debug des événements système, authentification et erreurs
        </Typography>
      </Box>

      {/* Statistiques */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total des logs
              </Typography>
              <Typography variant="h4">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Erreurs
              </Typography>
              <Typography variant="h4" color="error">
                {stats.bySeverity.error || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Warnings
              </Typography>
              <Typography variant="h4" color="warning.main">
                {stats.bySeverity.warning || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Auth/Token
              </Typography>
              <Typography variant="h4" color="primary">
                {(stats.byType.auth || 0) + (stats.byType.token || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtres */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Filtres</Typography>
        </Box>
        
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={filters.type}
                onChange={handleFilterChange('type')}
                label="Type"
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="auth">Auth</MenuItem>
                <MenuItem value="token">Token</MenuItem>
                <MenuItem value="error">Error</MenuItem>
                <MenuItem value="system">System</MenuItem>
                <MenuItem value="onedrive">OneDrive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Sévérité</InputLabel>
              <Select
                value={filters.severity}
                onChange={handleFilterChange('severity')}
                label="Sévérité"
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="info">Info</MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="warning">Warning</MenuItem>
                <MenuItem value="error">Error</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.5}>
            <TextField
              fullWidth
              size="small"
              label="User ID"
              value={filters.userId}
              onChange={handleFilterChange('userId')}
              placeholder="Filtrer par User ID"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.5}>
            <TextField
              fullWidth
              size="small"
              label="Email utilisateur"
              value={filters.userEmail}
              onChange={handleFilterChange('userEmail')}
              placeholder="Filtrer par email"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={resetFilters}
                size="small"
              >
                Reset
              </Button>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={loadLogs}
                size="small"
              >
                Actualiser
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={exportLogs}
                size="small"
                disabled={logs.length === 0}
              >
                Export
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Messages d'erreur */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tableau des logs */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Sévérité</TableCell>
                <TableCell>Utilisateur</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Détails</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedLogs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {formatDate(log.timestamp)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={log.type}
                      color={typeColors[log.type] || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={log.severity}
                      color={severityColors[log.severity] || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {log.displayName || log.userEmail?.split('@')[0] || 'Utilisateur'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {log.userEmail}
                      </Typography>
                      {log.userId && log.userId !== 'unknown' && (
                        <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', display: 'block' }}>
                          {log.userId.startsWith('azure-') ? log.userId.substring(6, 16) + '...' : log.userId}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {log.message}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {Object.keys(log.details || {}).length > 0 
                        ? JSON.stringify(log.details).substring(0, 100) + '...'
                        : '-'
                      }
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={logs.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Lignes par page:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}–${to} sur ${count !== -1 ? count : `plus de ${to}`}`
          }
        />
      </Paper>
    </Box>
  );
};

export default SystemLogsPage;